import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import Shell from '../components/TopBarShell';
import { Button } from '../components/ui/button';
import { useT } from '../contexts/LocaleProvider';
import api from '../utils/api';

/**
 * OAuth landing page · Google redirects here with ?code=...&state=... after
 * the user grants Calendar access. We forward both to the backend, which
 * exchanges the code for tokens and stores the connection.
 */
export default function GoogleCalendarCallback() {
  const t = useT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState('exchanging'); // exchanging | ok | error
  const [error, setError] = useState('');
  const [googleEmail, setGoogleEmail] = useState('');

  useEffect(() => {
    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');

    if (oauthError) {
      setStatus('error');
      setError(oauthError);
      return;
    }
    if (!code || !state) {
      setStatus('error');
      setError(t('gcal.callback.missing'));
      return;
    }
    let cancelled = false;
    api.googleCalendarOAuthCallback(code, state)
      .then((data) => {
        if (cancelled) return;
        setGoogleEmail(data.google_email || '');
        setStatus('ok');
      })
      .catch((e) => {
        if (cancelled) return;
        setStatus('error');
        setError(e.response?.data?.detail || t('gcal.callback.failed'));
      });
    return () => { cancelled = true; };
  }, [params, t]);

  return (
    <Shell>
      <div className="max-w-md mx-auto bg-[hsl(var(--surface))] border border-border rounded-lg p-8 mt-10 text-center">
        {status === 'exchanging' && (
          <>
            <Loader2 className="h-7 w-7 text-[hsl(var(--text-muted))] animate-spin mx-auto" />
            <h1 className="font-display text-[1.25rem] mt-4">{t('gcal.callback.exchanging')}</h1>
          </>
        )}
        {status === 'ok' && (
          <>
            <CheckCircle2 className="h-8 w-8 text-[hsl(var(--success))] mx-auto" />
            <h1 className="font-display text-[1.25rem] mt-4">{t('gcal.callback.ok')}</h1>
            {googleEmail && (
              <p className="text-[14px] text-[hsl(var(--text-soft))] mt-2 font-num">{googleEmail}</p>
            )}
            <p className="text-[13px] text-[hsl(var(--text-muted))] mt-3">{t('gcal.callback.next')}</p>
            <div className="mt-5">
              <Button onClick={() => navigate('/calendar', { replace: true })}>
                {t('gcal.callback.backToCalendar')}
              </Button>
            </div>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertCircle className="h-8 w-8 text-[hsl(var(--danger))] mx-auto" />
            <h1 className="font-display text-[1.25rem] mt-4">{t('gcal.callback.errorTitle')}</h1>
            <p className="text-[13px] text-[hsl(var(--text-soft))] mt-2 break-words">{error}</p>
            <div className="mt-5">
              <Button variant="outline" onClick={() => navigate('/calendar', { replace: true })}>
                {t('gcal.callback.backToCalendar')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}
